import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "investor" });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(t("reg_demo"));
      if (u.role === "landlord") navigate("/landlord");
      else if (u.role === "investor") navigate("/marketplace");
      else if (u.role === "amc") navigate("/amc");
      else navigate("/tenant");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-12">
      <div className="col-span-12 md:col-span-5 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("reg_tag")}</div>
            <h1 className="font-display text-4xl font-black mt-2">{t("reg_title")}</h1>
          </div>

          <div className="space-y-3">
            <Label className="font-mono-label text-[10px]">{t("reg_role")}</Label>
            <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}
              className="grid grid-cols-2 gap-2">
              {[
                { v: "landlord", l: t("reg_landlord") },
                { v: "investor", l: t("reg_investor") },
                { v: "tenant",   l: t("reg_tenant") },
                { v: "amc",      l: t("reg_amc") },
              ].map((o) => (
                <label key={o.v} data-testid={`role-${o.v}`}
                  className={`border p-3 cursor-pointer text-center font-mono-label text-[10px] ${
                    form.role === o.v ? "border-black bg-black text-white" : "border-black/20 hover:border-black"
                  }`}>
                  <RadioGroupItem value={o.v} className="hidden" />
                  {o.l}
                </label>
              ))}
            </RadioGroup>

            <Label className="font-mono-label text-[10px]">{t("reg_name")}</Label>
            <Input data-testid="reg-name" required value={form.name} onChange={update("name")} className="rounded-none h-11"/>
            <Label className="font-mono-label text-[10px]">{t("login_email")}</Label>
            <Input data-testid="reg-email" type="email" required value={form.email} onChange={update("email")} className="rounded-none h-11"/>
            <Label className="font-mono-label text-[10px]">{t("login_password")}</Label>
            <Input data-testid="reg-password" type="password" required minLength={4} value={form.password} onChange={update("password")} className="rounded-none h-11"/>
          </div>
          <Button data-testid="reg-submit" disabled={busy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F70] h-11 font-mono-label text-xs">
            {busy ? t("processing") : t("reg_submit")}
          </Button>
          <div className="text-sm text-zinc-600">
            {t("reg_have_account")} <Link to="/login" className="text-[#002FA7] underline">{t("reg_signin")}</Link>
          </div>
        </form>
      </div>
      <div className="hidden md:block col-span-7 bg-[#002FA7] relative overflow-hidden">
        <div className="absolute inset-0 lm-grid-bg opacity-30" />
        <div className="absolute bottom-10 start-10 text-white max-w-md">
          <div className="font-mono-label text-[10px] text-[#FFCC00]">⎯⎯ {t("reg_demo")}</div>
          <div className="font-display text-3xl font-black mt-3 leading-tight">
            {t("reg_demo_body")}
          </div>
        </div>
      </div>
    </div>
  );
}
